import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { SpinRun } from '../../../Generated/SpinRun.ts'
import { useSpinRuns } from './useSpinData.js'

/**
 * Shared run/taxonomy selection for every SPIN page, carried in the URL query (`?run=<id>&tax=<amplicon>`)
 * so it survives reloads, is shareable, and persists across the dashboard→sub-page navigation. Wraps
 * the (self-contained, cheap) `useSpinRuns` list and resolves the selected run BY ID — never by list
 * index, which is unstable across reloads/filtering.
 *
 * Resolution order for the selected run: the `?run=` id if still present in the list → else the latest
 * run of the `?tax=` amplicon (runs arrive sorted started_at DESC) → else the newest run overall.
 */
export const useSelectedRun = () => {
  const [params, setParams] = useSearchParams()
  const { runs, loading, error } = useSpinRuns()

  const tax = params.get('tax') ?? '' // '' = all amplicons
  const runId = params.get('run')

  const amplicons = useMemo(
    () => [...new Set(runs.map((r) => r.amplicon).filter((a): a is string => !!a))],
    [runs]
  )
  // The run dropdown honours the taxonomy filter.
  const runOptions = useMemo(() => runs.filter((r) => !tax || r.amplicon === tax), [runs, tax])

  const selected: SpinRun | null =
    (runId ? runs.find((r) => r.id === runId) : undefined) ??
    (tax ? runs.find((r) => r.amplicon === tax) : undefined) ??
    runs[0] ??
    null

  const setRun = (id: string) =>
    setParams(
      (p) => {
        p.set('run', id)
        return p
      },
      { replace: true }
    )

  // Choosing a taxonomy drops the explicit run so resolution jumps to that amplicon's latest run
  // (mirrors the old onTaxChange behaviour); clearing it ('' = All) jumps to the newest run overall.
  const setTax = (value: string) =>
    setParams(
      (p) => {
        if (value) p.set('tax', value)
        else p.delete('tax')
        p.delete('run')
        return p
      },
      { replace: true }
    )

  return { runs, selected, tax, amplicons, runOptions, setRun, setTax, loading, error }
}
